/**
 * What a workflow is FOR: the document types and departments whose documents
 * follow it. The rule, said once on the page so nobody has to guess it:
 * the most specific workflow wins, and two may not cover the same thing.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MultiSelect } from '../../shared/MultiSelect';
import { useSetCoverage, type ApprovalWorkflow } from './approvalsData';
import { coverIds, fallsToCatchAll } from './coverage';
import { isCallFailure } from '../../lib/callable';
import { COVERAGE_IDS_MAX } from '../../types/fieldLimits';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

interface Named { id: string; name: string }

interface Props {
  workflow: ApprovalWorkflow;
  all: readonly ApprovalWorkflow[];
  types: readonly Named[];
  departments: readonly Named[];
  onSaved: (message: string) => void;
}

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

export function WorkflowCoverage({ workflow, all, types, departments, onSaved }: Props) {
  const saved = coverIds(workflow);
  const [typeIds, setTypeIds] = useState<string[]>(saved.types);
  const [departmentIds, setDepartmentIds] = useState<string[]>(saved.departments);
  const save = useSetCoverage();

  // A different workflow, or the saved coverage changed under us: start from what is saved.
  const savedKey = `${workflow.workflowId}|${saved.types.join(',')}|${saved.departments.join(',')}`;
  useEffect(() => {
    setTypeIds(saved.types);
    setDepartmentIds(saved.departments);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  if (workflow.isDefault) {
    const left = fallsToCatchAll(all, types, departments);
    const everything = left.types.length === types.length && left.departments.length === departments.length;
    return (
      <section className={styles.section} aria-labelledby="covers-title">
        <h2 className={styles.sectionTitle} id="covers-title">What it is for</h2>
        {/* Named outright. "Everything else" is true but leaves an administrator
            wondering where the coverage fields went on this one page. */}
        <p className={own.headPills}><span className={`${own.pill} ${own.pillOn}`}>Catch-all workflow</span></p>
        <p className={`${own.itemMeta} ${own.explain}`}>
          This workflow has no document-type or department coverage of its own. It is used only when no
          more specific active workflow with steps matches the document, so a document is never left
          without a chain.
        </p>
        <p className={own.precedence} aria-label="Routing order, most specific first">
          Document type + department &rarr; Document type &rarr; Department &rarr; Catch-all
        </p>
        <p className={own.itemMeta} style={{ marginTop: 'var(--sp-3)' }}>
          <Link to="/admin/approval-workflows">Check workflow routing</Link> on the workflows page, where you
          can try a document type and department and see which workflow it would follow.
        </p>
        {everything ? (
          <p className={own.itemMeta}>
            Right now that is every document: no other workflow covers anything. To send some kinds of
            document through a different chain, create another workflow below and say what it is for.
          </p>
        ) : (
          <>
            <p className={own.itemMeta}>Today that means these, unless a workflow covers them together with something else:</p>
            <ul className={own.fallsList} aria-label="Document types that follow this workflow">
              {left.types.map((t) => <li key={t.id}>{t.name}</li>)}
            </ul>
          </>
        )}
      </section>
    );
  }

  const dirty = !same(typeIds, saved.types) || !same(departmentIds, saved.departments);
  const both = typeIds.length > 0 && departmentIds.length > 0;
  const tooMany = typeIds.length > COVERAGE_IDS_MAX || departmentIds.length > COVERAGE_IDS_MAX;

  return (
    <section className={styles.section} aria-labelledby="covers-title">
      <h2 className={styles.sectionTitle} id="covers-title">What it is for</h2>
      <p className={`${own.itemMeta} ${own.explain}`}>
        Choose the document types, the departments, or both. A document follows the most specific
        workflow that covers it. Department means the first department the document is filed under.
      </p>
      <p className={own.precedence} aria-label="Routing order, most specific first">
        Document type + department &rarr; Document type &rarr; Department &rarr; Catch-all
      </p>
      <div className={own.coverGrid}>
        <div>
          <div className={own.label} aria-hidden="true">Document types</div>
        <MultiSelect
          id="cover-types"
          label="Document types"
          options={types}
          value={typeIds}
          onChange={setTypeIds}
          placeholder="Any document type"
          disabled={save.isPending}
        />
        </div>
        <div>
          <div className={own.label} aria-hidden="true">Departments</div>
        <MultiSelect
          id="cover-departments"
          label="Departments"
          options={departments}
          value={departmentIds}
          onChange={setDepartmentIds}
          placeholder="Any department"
          disabled={save.isPending}
        />
        </div>
      </div>
      {both && (
        <p className={own.itemMeta}>
          Both are chosen, so a document needs one of these types <strong>and</strong> one of these
          departments. For &ldquo;either&rdquo;, make two workflows.
        </p>
      )}
      {!both && typeIds.length === 0 && departmentIds.length === 0 && (
        <p className={own.itemMeta}>Nothing is chosen, so no document follows this workflow yet.</p>
      )}
      {tooMany && <p className={own.error}>Choose at most {COVERAGE_IDS_MAX} of each.</p>}
      {save.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(save.error) || save.error instanceof Error ? save.error.message : 'That could not be saved.'}
        </div>
      )}
      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={!dirty || save.isPending || tooMany}
          onClick={() =>
            save.mutate(
              { workflowId: workflow.workflowId, documentTypeIds: typeIds, departmentIds },
              { onSuccess: () => onSaved(`Saved. Documents submitted from now on are routed by it; any already in approval finish on the chain they started with.`) },
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save what it is for'}
        </button>
        {dirty && !save.isPending && (
          <button type="button" className={styles.btn} onClick={() => { setTypeIds(saved.types); setDepartmentIds(saved.departments); save.reset(); }}>
            Undo changes
          </button>
        )}
      </div>
    </section>
  );
}
